import { Col, Container, Row } from "reactstrap";
import MainLayout from "../../layouts/MainLayout";
import OfficeCard from "../../components/Cards/OfficeCard";
import { useEffect, useState } from "react";
import { API_URL } from "../../utils/constants";
import { IoSearch } from "react-icons/io5";
import OfficesSearchModal from "./Modals/search";
import IconButton from "../../components/Buttons/Icon";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

function OfficesPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [officesLoading, setOfficesLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [offices, setOffices] = useState([]);
  const [totalOffices, setTotalOffices] = useState(0);
  const [keyword, setKeyword] = useState(new URLSearchParams(location.search).get('keyword') || '');
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMorePages, setHasMorePages] = useState(false);

  const getOffices = (page = 1, append = false) => {
    const baseCacheKey = `offices_data_${keyword}`;
    const cachedData = sessionStorage.getItem(baseCacheKey);

    // Check if we have cached data and we're loading the first page
    if (!append && page === 1 && cachedData) {
      const parsed = JSON.parse(cachedData);
      setOffices(parsed.offices);
      setTotalOffices(parsed.totalOffices);
      setHasMorePages(parsed.hasMorePages);
      setCurrentPage(parsed.currentPage);
      setOfficesLoading(false);
      return;
    }

    if (append) {
      setLoadingMore(true);
    } else {
      setOfficesLoading(true);
    }

    fetch(`${API_URL}/offices?page=${page}&keyword=${encodeURIComponent(keyword)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    })
    .then(response => response.json())
    .then(data => {
      if (data.success) {
        let updatedOffices;
        
        if (append) {
          updatedOffices = [...offices, ...data.offices.data];
          setOffices(updatedOffices);
        } else {
          updatedOffices = data.offices.data;
          setOffices(updatedOffices);
        }

        setTotalOffices(data?.offices?.total || 0);
        
        // Check if there are more pages
        const morePages = data.offices.current_page < data.offices.last_page;
        setHasMorePages(morePages);
        setCurrentPage(data.offices.current_page);

        // Always update cache with all loaded offices
        const cacheData = {
          offices: updatedOffices,
          totalOffices: data?.offices?.total || 0,
          hasMorePages: morePages,
          currentPage: data.offices.current_page
        };
        sessionStorage.setItem(baseCacheKey, JSON.stringify(cacheData));
      }
      setOfficesLoading(false);
      setLoadingMore(false);
    })
    .catch(error => {
      console.error('Error fetching offices:', error);
      setOfficesLoading(false);
      setLoadingMore(false);
    });
  }

  const loadMore = () => {
    if (!loadingMore && hasMorePages) {
      getOffices(currentPage + 1, true);
    }
  }

  // Infinite scroll handler
  useEffect(() => {
    const handleScroll = () => {
      if (loadingMore || !hasMorePages) return;

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;

      if (scrollTop + clientHeight >= scrollHeight - 200) {
        loadMore();
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loadingMore, hasMorePages, currentPage]);

  useEffect(() => {    
    const urlKeyword = new URLSearchParams(location.search).get('keyword') || '';
    setKeyword(urlKeyword);
    setCurrentPage(1);

    setTimeout(() => {
      getOffices(1, false);
    }, 100);
  }, [location.search]);

  return (
    <MainLayout>
      <section className="py-4">
        <Container>
          <Row className="align-items-center mb-3">
            <Col xs="8">
              <h3 className="fw-normal">
                { t('offices.title') }{' '}
                <small className="text-muted">({totalOffices})</small>
              </h3>
            </Col>
            <Col xs="4" className="text-end">
              <IconButton className="rounded-pill" onClick={() => setIsSearchModalOpen(true)}>
                <IoSearch size={20} /> 
              </IconButton>
            </Col>
          </Row>

          {officesLoading ? (
            <div className="loader-container">
              <div className="loader"></div>
            </div>
          ) : (
            <>
              <Row className="g-3">
                {offices?.length > 0 ? (
                  offices.map((data, key) => {
                    return (
                      <Col md="4" sm="6" lg="3" xs="12" key={key}>
                        <OfficeCard office={data} />
                      </Col>
                    );
                  })
                ) : (
                  <Col md="12" className="text-center py-5">
                    <h6 className="mt-4 fw-normal text-muted">لا توجد مكاتب</h6>
                  </Col>
                )}
              </Row>

              {loadingMore && (
                <Row className="justify-content-center my-4">
                  <Col lg="12" className="text-center">
                    <div className="loader mx-auto"></div>
                  </Col>
                </Row>
              )}
            </>
          )}
        </Container>

        <OfficesSearchModal 
          isOpen={isSearchModalOpen}
          keyword={keyword}
          onKeywordChange={(e) => setKeyword(e.target.value)}
          toggle={() => setIsSearchModalOpen(!isSearchModalOpen)}
          onSearch={() => {
            navigate(`?keyword=${encodeURIComponent(keyword)}`);
            setIsSearchModalOpen(false);
          }}
        />
      </section>
    </MainLayout>
  );
}

export default OfficesPage;